/**
 * compliance-brief.ts — Editorial copy layer for the Baseline Compliance
 * pillar standfirst.
 *
 * Mirrors `posture-brief.ts` in shape and voice. Every editorial slot is ONE
 * deterministic, data-driven line. There is no variant table and no
 * rotation — the same input always produces the same brief, and all
 * variation in what the user reads comes from the data (score, delta,
 * drifting workloads, top action, critical count), never from picking among
 * synonymous phrasings of the same fact.
 *
 * The subject is "Baseline" — the brief describes the compliance pillar
 * score and its 30-day movement, NOT the count of new drifts opened (that
 * lives in Section · 01 on the page, driven by `summarizeDriftTrend`).
 * Keeping two distinct nouns ("baseline" for score-movement copy, "drift"
 * for queue-volume copy) avoids the user-visible contradiction where the
 * brief once described drift as flat while the chart underneath showed
 * drift climbing — different stats, same word.
 *
 * The verdict routes off `bucket` (the five-state delta bucket), as does the
 * headline's cadence line, so they agree about which way the score is moving.
 *
 * Action-first fields: `headline` names the top setting, else the top
 * action, else the first drifting workload, else the critical count, else
 * the all-clear, cadence or queue line. `lede` names the drifted-resource
 * count (with the top setting), and falls back to the drifting workloads
 * and the score.
 *
 * Loading-state handling: when `input.score` is null/undefined, the composer
 * short-circuits to the neutral "Reading baseline…" brief rather than
 * coercing null to 0 and flashing a worst-band verdict before the real score
 * arrives.
 *
 * Unknown-score handling (`noData`): when the score is null because the stats
 * call failed, nothing has been scanned, or the baseline is still being
 * captured, the brief says why and what to do next. Unknown is never zero:
 * no number, no all-clear, no resource count in the provenance line and no
 * delta.
 * Only the unscanned brief has an action, by viewer and schedule
 * (`firstAssessmentAction`): the first scan for a Platform Admin
 * (`firstScanHref`), Tenant Management for a viewer who may turn a known-off
 * schedule on (`scheduleSettingsHref`), no button for anyone else.
 */

import {
  cap,
  briefProvenance,
  bucketDelta,
  driftedLedeBody,
  FIRST_ASSESSMENT,
  firstAssessmentAction,
  isNegative,
  knownCount,
  LEDE_FRAGMENTS,
  type DeltaBucket,
  type ScanScheduleState,
} from './brief-helpers'
import type { ActionBriefFields } from './brief-shape'
import { settingLabel } from './drift-names'
import { resourcesCheckedScope } from './drift-provenance'

export type Pillar = 'compliance' | 'bpa' | 'identity'

/** Why the baseline score is unknown: the stats call failed, nothing has been
 *  scanned, or the baseline is still being captured. */
export type ComplianceNoData = 'failed' | 'unscanned' | 'incomplete'

export interface ComplianceBrief {
  /** Six words or fewer. Not drawn: the Carbon pillar brief shows no verdict (D22). */
  verdict: string
  /**
   * The provenance line (D36): "Assessed by Guardian at 06:12 today · 1,050
   * resources checked", "Awaiting first assessment", or "" when the
   * assessment time is not known. PillarBrief renders it as the helper line
   * with the iuvo mark.
   */
  byline: string
  /** Score delta vs N days ago, for PillarBrief's delta tag on the score tile. `null` when the score is unknown (`noData`). */
  delta: { value: number; days: number } | null
  /** Action-first h1 (blueprint §6.2 row 2). */
  headline: string
  /** The setting or workload the headline names, set at 400 (never a pillar ink). */
  emphasis?: ActionBriefFields['emphasis']
  /** One sentence, one `**bold**` run naming the subject and its count. */
  lede: string
  /** The view's one Soft Green button; only with a `primaryHref` and open drift to review, or `firstScanHref` on an unscanned tenant. */
  primaryAction?: ActionBriefFields['primaryAction']
}

export interface ComplianceBriefInput {
  /**
   * The tenant's last real assessment (`lastAssessmentAt`,
   * lib/tenant-assessed.ts), for the provenance line. Null reads "Awaiting
   * first assessment"; undefined (not known) draws no line.
   */
  assessedAt?: string | null
  /** Resources the baseline checks (`complianceScore.totalChecks`): the provenance line's scope. */
  resourcesChecked?: number
  /** The clock for "today" / "yesterday" in the provenance line; tests pin it. */
  now?: Date
  /**
   * Active tenant ID. Callers still pass this; unused by the composer now
   * that copy is deterministic rather than varying per tenant per day.
   */
  tenantId?: string
  /** Compliance pillar score (0-100). */
  score?: number | null
  /** 30-day score delta — drives the verdict, the headline and the `delta` field. */
  deltas?: { score?: number; days?: number }
  /**
   * Highest-impact priority drift / action — informs the headline and lede.
   * Two shapes:
   *   - action-shaped: `title` is an imperative ("Restore the Exchange
   *     transport rule baseline") and is embedded as-is.
   *   - setting-shaped: `title` is the humanised resource name ("Entra
   *     Conditional Access Policy", `driftResourceName`) and `setting` the
   *     drifted property as stored ("AutoForwardEnabled"); the composer reads
   *     it through `settingLabel` ("Auto-forward enabled") and writes the
   *     sentence around them. Callers must not pre-join them with a " · "
   *     label separator — that idiom is for tooltips, not prose.
   */
  topAction?: { pillar: Pillar; title: string; setting?: string; impact?: 'crit' | 'high' | 'med' }
  /** Open critical-severity drift count — drives the verdict and headline. */
  criticalCount?: number
  /**
   * Canonical workload names with active drifts — e.g. "Identity",
   * "SharePoint". Map from M365DSC component prefixes via
   * `getWorkloadForDriftComponent`; do not pass resource names or M365DSC
   * resource codes here. The composer names them directly when 1-2 workloads
   * carry the drift; 3+ falls through to a workload count, because a
   * workload is a named subject, not a unit you tally.
   */
  driftingWorkloads?: string[]
  /**
   * Resources with active drift (data-map C10: `dashboardStats.activeDrifts`,
   * mapped from `metrics.ActiveAlerts`; the unit is resources, not settings).
   * Drives the lede. Pass `undefined` while the stats query loads, never 0:
   * the lede then falls back to the drifting workloads and the score.
   */
  driftedResources?: number
  /** Where the primary button goes (the composer owns no routes). Without it,
   *  or on an all-clear or calm headline, the brief has no `primaryAction`. */
  primaryHref?: string
  /** Why the score is unknown (not loading). Only read when `score` is null;
   *  leave it unset while the stats query loads. */
  noData?: ComplianceNoData
  /**
   * Where the viewer can run the first scan: Ad-Hoc Scans (`/admin/scans`),
   * which only a Platform Admin can open (App.tsx `PlatformAdminRoute`; the
   * scan endpoint 403s everyone else). Pass it only for such a viewer. Read
   * only with `noData: 'unscanned'`, where it becomes the brief's one action.
   */
  firstScanHref?: string
  /** With `noData: 'unscanned'`: Tenant Management, passed only for a viewer
   *  who may edit the scan schedule (GlobalAdmin or SecurityAdmin). */
  scheduleSettingsHref?: string
  /** With `noData: 'unscanned'`: the tenant's scan schedule (`useTenantScanSchedule`); 'pending' or omitted claims none. */
  scanSchedule?: ScanScheduleState
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

/** Strip trailing sentence punctuation from an action title before it's
 *  re-embedded in a new sentence (avoids "Enforce MFA.. first."). */
function stripTrailingPunct(s: string): string {
  return s.replace(/[.,;:!?]+$/, '')
}

/** Word form for 1-9, digits above 9 — keeps drift/workload counts reading
 *  as prose rather than a bare digit at the front of a sentence. */
const NUMBER_WORDS: readonly string[] = [
  'Zero',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
]

function numberWord(n: number): string {
  return Number.isInteger(n) && n >= 0 && n <= 9 ? NUMBER_WORDS[n] : String(n)
}

/** Normalizes an optional critical-drift count to a finite non-negative
 *  integer, or `undefined` when the input can't honestly be treated as one. */
function normalizeCritical(n: number | undefined): number | undefined {
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : undefined
}

/** Round to one decimal, then render as an integer string when whole
 *  ("4") and one decimal place otherwise ("0.4", "2.5"). */
function formatDelta(x: number): string {
  const r = Math.round(x * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

/* -------------------------------------------------------------------------- */
/* Copy tables — one line per slot, tokens filled from data                   */
/* -------------------------------------------------------------------------- */

const VERDICT = {
  ROUTINE_WATCH: 'Routine watch only.',
  SCORE_DOWN: 'Score down {n} this month.',
  CRIT_ONE: 'One critical drift still open.',
  CRIT_FEW: '{n} critical drifts still open.',
  LEAD_NEXT: 'Take the lead drift next.',
  START_WORKLOAD: 'Start with {A}.',
  LEAD_FIRST: 'Take the lead drift first.',
  START_WORKLOAD_WEEK: 'Start with {A} this week.',
  WORK_QUEUE: 'Work the drift queue top down.',
  NO_DATA_FAILED: 'Baseline not loaded',
  NO_DATA_UNSCANNED: 'Not scanned yet',
  NO_DATA_INCOMPLETE: 'Baseline in progress',
}

/**
 * Action-first h1 (blueprint §6.2 row 2): the top setting, else the top
 * action, else the first drifting workload, else the critical count.
 * The subject of `TOP_SETTING` is a setting, so its emphasis never takes a
 * pillar ink. The all-clear and cadence lines never speak while resources
 * are known to be drifting.
 */
const HEADLINE_ACTION = {
  LOADING: 'Reading your baseline.',
  NO_DATA_FAILED: 'Reload the page to see your baseline.',
  // Unscanned: by viewer and schedule, from the shared FIRST_ASSESSMENT table
  // (`firstAssessmentAction`), the same words as the Command Center.
  NO_DATA_INCOMPLETE: 'Review the drift queue.',
  TOP_SETTING: 'Start with the {setting} setting.',
  TOP_ACTION: '{title} first.',
  WORKLOAD: 'Start with {A} this week.',
  CRIT_ONE: 'Close the critical drift first.',
  CRIT_FEW: 'Close the {n} critical drifts first.',
  CLEAR: 'Nothing to close this week.',
  CADENCE: 'Keep the usual review cadence this week.',
  QUEUE: 'Work the drift queue top down this week.',
}

/** The primary button names the top setting, else the drifted count, else the queue. */
const PRIMARY_ACTION = {
  SETTING: 'Review {setting}',
  DRIFTED_ONE: 'Review the drifted resource',
  DRIFTED_FEW: 'Review the {n} drifted resources',
  QUEUE: 'Review the drift queue',
}

/**
 * The lede. With `driftedResources` it names the drifted-resource count
 * (`LEDE_FRAGMENTS`), plus the top setting when the ranker named one: the
 * ranker orders by score impact, so the clause claims "moves your score
 * most", never recency. Without it, the fallback uses only facts the composer
 * already has: the drifting workloads and the baseline score.
 */
const LEDE = {
  LOADING: '**Guardian is reading your baseline**.',
  NO_DATA_FAILED: '**Guardian could not read your baseline**, so there is no score to show.',
  NO_DATA_UNSCANNED: '**Guardian has not assessed this tenant yet**, and shows your baseline score after the first assessment.',
  NO_DATA_INCOMPLETE: "**Guardian is still capturing this tenant's baseline**, so there is no score yet.",
  TOP_SETTING_CLAUSE: ', and the {setting} setting moves your score most',
  TOP_SETTING_IN_CLAUSE: ', and the {setting} setting in {resource} moves your score most',
  ONE_WORKLOAD: '**{A} is the one workload drifting**, with the baseline at {score}.',
  TWO_WORKLOAD: '**{A} and {B} are drifting**, with the baseline at {score}.',
  MANY_WORKLOAD: '**{n} workloads are drifting**, with the baseline at {score}.',
  SCORE: '**The baseline is at {score}**.',
}

/** Each unknown-score reason's key in the VERDICT and LEDE tables, and in
 *  HEADLINE_ACTION for all but 'unscanned' (`firstAssessmentAction`). */
const NO_DATA_KEY = {
  failed: 'NO_DATA_FAILED',
  unscanned: 'NO_DATA_UNSCANNED',
  incomplete: 'NO_DATA_INCOMPLETE',
} as const satisfies Record<ComplianceNoData, string>

/* -------------------------------------------------------------------------- */
/* Verdict (six words or fewer)                                               */
/* -------------------------------------------------------------------------- */

function scoreDeltaVerdict(n: string): string {
  return VERDICT.SCORE_DOWN.replace('{n}', n)
}

function pickVerdict(
  score: number,
  bucket: DeltaBucket,
  n: string,
  crit: number | undefined,
  workloads: string[],
  hasTopAction: boolean,
): string {
  const negative = isNegative(bucket)

  if (score >= 85) {
    if (negative) return scoreDeltaVerdict(n)
    const c = crit ?? 0
    if (c >= 1) return c === 1 ? VERDICT.CRIT_ONE : VERDICT.CRIT_FEW.replace('{n}', numberWord(c))
    if (workloads.length >= 1) return VERDICT.START_WORKLOAD.replace('{A}', workloads[0])
    if (hasTopAction) return VERDICT.LEAD_NEXT
    return VERDICT.ROUTINE_WATCH
  }

  if (score >= 70) {
    if (negative) return scoreDeltaVerdict(n)
    if (crit !== undefined && crit >= 1 && crit <= 6) {
      return crit === 1 ? VERDICT.CRIT_ONE : VERDICT.CRIT_FEW.replace('{n}', numberWord(crit))
    }
    return VERDICT.LEAD_NEXT
  }

  if (score >= 55) {
    if (workloads.length >= 1) return VERDICT.START_WORKLOAD.replace('{A}', workloads[0])
    return VERDICT.LEAD_FIRST
  }

  if (workloads.length >= 1) return VERDICT.START_WORKLOAD_WEEK.replace('{A}', workloads[0])
  return VERDICT.WORK_QUEUE
}

/* -------------------------------------------------------------------------- */
/* Action-first headline + lede (blueprint §6.2 row 2)                        */
/* -------------------------------------------------------------------------- */

/**
 * The subject of a setting-shaped top action: the setting's readable name
 * (`settingLabel`, the same helper the drift rows use), so the headline holds
 * no raw key and stays short. A one-word name ("State") names nothing on its
 * own, so it leads with its resource. `resource` is set when the subject
 * leaves the resource out, for the lede to name. Undefined for any other top
 * action.
 */
function topSetting(
  topAction: ComplianceBriefInput['topAction'] | undefined,
): { subject: string; resource?: string } | undefined {
  if (!topAction?.title || !topAction.setting) return undefined
  const title = stripTrailingPunct(topAction.title)
  const setting = settingLabel(stripTrailingPunct(topAction.setting))
  return setting.includes(' ') ? { subject: setting, resource: title } : { subject: `${title} ${setting.toLowerCase()}` }
}

function topSettingName(topAction: ComplianceBriefInput['topAction'] | undefined): string | undefined {
  return topSetting(topAction)?.subject
}

function pickActionHeadline(
  topAction: ComplianceBriefInput['topAction'] | undefined,
  workloads: string[],
  crit: number | undefined,
  score: number,
  bucket: DeltaBucket,
  drifted: number | undefined,
): { headline: string; emphasis?: ActionBriefFields['emphasis'] } {
  const setting = topSettingName(topAction)
  if (setting) {
    return { headline: HEADLINE_ACTION.TOP_SETTING.replace('{setting}', setting), emphasis: { text: setting } }
  }
  if (topAction?.title) {
    return { headline: HEADLINE_ACTION.TOP_ACTION.replace('{title}', cap(stripTrailingPunct(topAction.title))) }
  }
  if (workloads.length >= 1) {
    return { headline: HEADLINE_ACTION.WORKLOAD.replace('{A}', workloads[0]), emphasis: { text: workloads[0] } }
  }
  if (crit === 1) return { headline: HEADLINE_ACTION.CRIT_ONE }
  if (crit !== undefined && crit >= 2) return { headline: HEADLINE_ACTION.CRIT_FEW.replace('{n}', String(crit)) }
  const drifting = drifted !== undefined && drifted >= 1
  if (crit === 0 && score >= 85 && !drifting) return { headline: HEADLINE_ACTION.CLEAR }
  if (score >= 85 && !isNegative(bucket) && !drifting) return { headline: HEADLINE_ACTION.CADENCE }
  return { headline: HEADLINE_ACTION.QUEUE }
}

function buildLede(
  topAction: ComplianceBriefInput['topAction'] | undefined,
  workloads: string[],
  score: number,
  drifted: number | undefined,
  crit: number | undefined,
): string {
  if (drifted !== undefined && drifted >= 1) {
    const top = topSetting(topAction)
    const clause = !top
      ? ''
      : top.resource
        ? LEDE.TOP_SETTING_IN_CLAUSE.replace('{setting}', top.subject).replace('{resource}', top.resource)
        : LEDE.TOP_SETTING_CLAUSE.replace('{setting}', top.subject)
    return `${driftedLedeBody(drifted)}${clause}.`
  }
  // A known zero names the clean state only when nothing else says drift is open.
  if (drifted === 0 && workloads.length === 0 && !topAction?.title && (crit ?? 0) === 0) {
    return `${driftedLedeBody(0)}.`
  }
  const scoreStr = String(Math.round(score))
  if (workloads.length === 1) {
    return LEDE.ONE_WORKLOAD.replace('{A}', workloads[0]).replace('{score}', scoreStr)
  }
  if (workloads.length === 2) {
    return LEDE.TWO_WORKLOAD.replace('{A}', workloads[0]).replace('{B}', workloads[1]).replace('{score}', scoreStr)
  }
  if (workloads.length >= 3) {
    return LEDE.MANY_WORKLOAD.replace('{n}', String(workloads.length)).replace('{score}', scoreStr)
  }
  return LEDE.SCORE.replace('{score}', scoreStr)
}

function primaryLabel(setting: string | undefined, drifted: number | undefined): string {
  if (setting) return PRIMARY_ACTION.SETTING.replace('{setting}', setting)
  if (drifted !== undefined && drifted >= 1) {
    return drifted === 1 ? PRIMARY_ACTION.DRIFTED_ONE : PRIMARY_ACTION.DRIFTED_FEW.replace('{n}', String(drifted))
  }
  return PRIMARY_ACTION.QUEUE
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose a ComplianceBrief from the active Compliance pillar state. All
 * inputs are optional — the function degrades gracefully toward a plausible
 * "no data" brief rather than throwing.
 *
 * When `input.score` is null/undefined (e.g. the page mounted before the
 * score query resolved) we return a neutral "reading" brief rather than
 * synthesizing a false worst-band verdict. Without this guard the
 * composer would coerce `null → 0`, which lands the loading state in the
 * lowest editorial band and shows the user an authoritative-but-wrong
 * verdict for a fraction of a second before the real data arrives.
 *
 * Note: `score === 0` is a legitimate real value and still routes through
 * the normal band logic — only `null`/`undefined` triggers the loading brief.
 */
export function composeComplianceBrief(input: ComplianceBriefInput = {}): ComplianceBrief {
  const deltaValue = typeof input.deltas?.score === 'number' ? input.deltas.score : 0
  const deltaDays = typeof input.deltas?.days === 'number' ? input.deltas.days : 30

  // The provenance line (D36). The resource count rides only with a known
  // score: an unknown baseline's monitored count is not one to quote.
  const byline = briefProvenance(input.assessedAt, {
    scope: input.score == null ? undefined : resourcesCheckedScope(input.resourcesChecked),
    now: input.now,
  })

  if (input.score == null && input.noData) {
    const key = NO_DATA_KEY[input.noData]
    // Only the unscanned brief has an action: what the viewer can do about
    // the first assessment. A failed or incomplete baseline offers none.
    const action: ReturnType<typeof firstAssessmentAction> =
      input.noData === 'unscanned'
        ? firstAssessmentAction({
            scanHref: input.firstScanHref,
            settingsHref: input.scheduleSettingsHref,
            schedule: input.scanSchedule,
          })
        : { headline: HEADLINE_ACTION[NO_DATA_KEY[input.noData]] }
    return {
      verdict: VERDICT[key],
      byline,
      delta: null,
      headline: action.headline,
      lede: LEDE[key],
      ...(action.primaryAction ? { primaryAction: action.primaryAction } : {}),
    }
  }

  if (input.score == null) {
    return {
      verdict: 'Reading baseline…',
      byline,
      delta: { value: deltaValue, days: deltaDays },
      headline: HEADLINE_ACTION.LOADING,
      lede: LEDE.LOADING,
    }
  }

  const score = input.score
  const bucket = bucketDelta(deltaValue)
  const n = formatDelta(Math.abs(deltaValue))

  const workloads: string[] = (input.driftingWorkloads ?? [])
    .filter((w): w is string => typeof w === 'string' && w.trim().length > 0)
    .map(w => w.trim())

  const crit = normalizeCritical(input.criticalCount)

  const verdict = pickVerdict(score, bucket, n, crit, workloads, !!input.topAction?.title)

  const drifted = knownCount(input.driftedResources)
  const action = pickActionHeadline(input.topAction, workloads, crit, score, bucket, drifted)
  const lede = buildLede(input.topAction, workloads, score, drifted, crit)
  const href = input.primaryHref?.trim()
  const calm = action.headline === HEADLINE_ACTION.CLEAR || action.headline === HEADLINE_ACTION.CADENCE

  return {
    verdict,
    byline,
    delta: { value: deltaValue, days: deltaDays },
    headline: action.headline,
    ...(action.emphasis ? { emphasis: action.emphasis } : {}),
    lede,
    ...(href && !calm
      ? { primaryAction: { label: primaryLabel(topSettingName(input.topAction), drifted), href } }
      : {}),
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Copy tables exposed for exact-string assertions in tests (and for a
 *  generic copy-lint sweep). Not part of the public runtime API; do not
 *  import from app code. */
export const __TEST_ONLY__ = {
  VERDICT,
  HEADLINE_ACTION,
  PRIMARY_ACTION,
  LEDE,
  LEDE_FRAGMENTS,
  FIRST_ASSESSMENT,
}
